import axios from "axios";
import { CryptoJSAesEncrypt, getDeviceId, AkdCrypt256b64 } from "../helpers/cryp";
import { deviceDetect, isDesktop, deviceType } from "react-device-detect";
import CryptoJS from "crypto-js";
import axiosInstance from "../helpers/axiosInstance";

const BASE_URL = process.env.REACT_APP_API_URL;
const { osName } = deviceDetect();

const authHeader = (token) => (token ? { Authorization: `Bearer ${token}` } : {});

export const doLogin = async (data = {}) => {
  if (!BASE_URL) {
    throw new Error("REACT_APP_API_URL tanımlı değil. .env’i kontrol edip dev server’ı yeniden başlatın.");
  }
  if (!data.login) throw new Error("login zorunlu");
  if (!data.password && !data.passwordPlain) throw new Error("password veya passwordPlain zorunlu");

  const staticData = {
    appCode: String(process.env.REACT_APP_CODE ?? ""),
    // appVersion: data.appVersion ?? process.env.REACT_APP_VERSION ?? "",
  
    deviceUuid: getDeviceId(),
    deviceName: osName === undefined ? "Unknown" : osName,
    deviceType: isDesktop ? "PC" : deviceType,
  };

  const plainCandidate = data.passwordPlain ?? data.password ?? "";
  const isExplicitlyEncrypted = data.isEncrypted === true;

  const looksUrlSafeB64 = (s) => {
    if (typeof s !== "string" || s.length === 0) return false;
    if (!/^[A-Za-z0-9\-_]+={0,2}$/.test(s)) return false;
    try {
      let std = s.replace(/-/g, "+").replace(/_/g, "/");
      while (std.length % 4) std += "=";
      atob(std);
      return true;
    } catch {
      return false;
    }
  };

  let passwordForRequest;
  if (data.passwordPlain != null && String(data.passwordPlain).length > 0) {
    passwordForRequest = AkdCrypt256b64(String(data.passwordPlain));
  } else if (!isExplicitlyEncrypted && !looksUrlSafeB64(plainCandidate)) {
    passwordForRequest = AkdCrypt256b64(String(plainCandidate));
  } else {
    passwordForRequest = String(plainCandidate);
  }

  const postBody = {
    ...data,
    ...staticData,
    password: passwordForRequest,
  };
  delete postBody.passwordPlain;
  delete postBody.isEncrypted;

  const signatureString =
    (postBody.login ?? "") +
    (postBody.password ?? "") +
    (postBody.appCode ?? "") +
    (postBody.appVersion ?? "") +
    (postBody.deviceUuid ?? "") +
    (postBody.deviceName ?? "") +
    (postBody.deviceType ?? "") +
    (postBody.connectionCode ?? "");

  const SIGNATURE = CryptoJSAesEncrypt(signatureString);

  try {
    const res = await axios.post(`${BASE_URL}/Membership/login/v2`, postBody, {
      headers: { signature: SIGNATURE },
      timeout: 15000,
    });
    return res;
  } catch (err) {
    // 1024 / illegal base64 fallback: SHA256(plain Latin1) → AkdCrypt256b64
    const code = err?.response?.data?.error?.code;
    const more = err?.response?.data?.error?.more || "";
    const msg  = err?.response?.data?.error?.message || "";
    const isB64Issue = code === 1024 && /illegal base64/i.test(`${more} ${msg}`);

    if (!isB64Issue || (data.passwordPlain == null && looksUrlSafeB64(plainCandidate))) {
      throw err;
    }

    const digestLatin1 = CryptoJS.enc.Latin1.stringify(CryptoJS.SHA256(String(plainCandidate)));
    const fallbackPassword = AkdCrypt256b64(digestLatin1);

    const postBody2 = { ...postBody, password: fallbackPassword };
    const signatureString2 =
      (postBody2.login ?? "") +
      (postBody2.password ?? "") +
      (postBody2.appCode ?? "") +
      (postBody2.appVersion ?? "") +
      (postBody2.deviceUuid ?? "") +
      (postBody2.deviceName ?? "") +
      (postBody2.deviceType ?? "") +
      (postBody2.connectionCode ?? "");
    const SIGNATURE2 = CryptoJSAesEncrypt(signatureString2);

    const res2 = await axios.post(`${BASE_URL}/Membership/login/v2`, postBody2, {
      headers: { signature: SIGNATURE2 },
      timeout: 15000,
    });
    return res2;
  }
};

// FORGOT PASSWORD (public)
// export const forgetPassword = async ({ email }) => {
//   if (!BASE_URL) throw new Error("REACT_APP_API_URL tanımlı değil.");
//   // const appCode = String(process.env.REACT_APP_CODE ?? "");0
//   if (!email) throw new Error("forgetPassword: email zorunludur.");

//   const postData = { email, appCode };
//   const signatureString = (postData.email ?? "") + (postData.appCode ?? "");
//   const SIGNATURE = CryptoJSAesEncrypt(signatureString);

//   return axios.post(`${BASE_URL}/Membership/renewPasswordMail`, postData, {
//     headers: { signature: SIGNATURE },
//     timeout: 15000,
//   });
// };

// FORGOT PASSWORD (public)
export const forgetPassword = async ({ email }) => {
  if (!BASE_URL) throw new Error("REACT_APP_API_URL tanımlı değil.");

  // HATA: Sondaki '0' kaldırıldı
  const appCode = String(process.env.REACT_APP_CODE ?? "");

  if (!email) throw new Error("forgetPassword: email zorunludur.");

  const postData = { email, appCode };

  // Dokümana göre: signature = AES(CBC) over sha256(email + appCode)
  const signatureString = (postData.email ?? "") + (postData.appCode ?? "");
  const SIGNATURE = CryptoJSAesEncrypt(signatureString);

  // İstersen consumer başlığını da ekleyebilirsin (opsiyonel ama tutarlı olur)
  return axiosInstance.post("/Membership/renewPasswordMail", postData, {
    headers: {
      signature: SIGNATURE,
      consumer: "apidoc",
    },
    timeout: 15000,
  });
};

// RENEW TOKEN (public)
export const renewToken = async ({ oldJwt }) => {
  if (!BASE_URL) throw new Error("REACT_APP_API_URL tanımlı değil.");
  const appCode = String(process.env.REACT_APP_CODE ?? "");
  if (!oldJwt) throw new Error("renewToken: oldJwt zorunludur.");

  const postData = { oldJwt, appCode };
  const signatureString = (postData.oldJwt ?? "") + (postData.appCode ?? "");
  const SIGNATURE = CryptoJSAesEncrypt(signatureString);

  return axios.post(`${BASE_URL}/Membership/renewToken`, postData, {
    headers: { signature: SIGNATURE },
    timeout: 15000,
  });
};

// USER PROFILE (protected)
export const getUserProfile = async (token) => {
  if (!BASE_URL) throw new Error("REACT_APP_API_URL tanımlı değil.");
  return axios.get(`${BASE_URL}/Membership/userProfile`, {
    headers: { ...authHeader(token) },
    timeout: 15000,
  });
};

// EDIT PROFILE (protected) — backend GET ise bıraktım, ihtiyaca göre PUT/PATCH yapılır
export const editProfile = async (token) => {
  if (!BASE_URL) throw new Error("REACT_APP_API_URL tanımlı değil.");
  return axios.get(`${BASE_URL}/Admin/user`, {
    headers: { ...authHeader(token) },
    timeout: 15000,
  });
};

// CHANGE PASSWORD (protected)
export const changePassword = async (data, token) => {
  if (!BASE_URL) throw new Error("REACT_APP_API_URL tanımlı değil.");
  return axios.put(`${BASE_URL}/Membership/changePassword`, data, {
    headers: { ...authHeader(token) },
    timeout: 15000,
  });
};

// LOGOUT (protected)
export const doLogout = async (token) => {
  if (!BASE_URL) throw new Error("REACT_APP_API_URL tanımlı değil.");
  return axios.post(`${BASE_URL}/Membership/logout`, null, {
    headers: { ...authHeader(token) },
    timeout: 15000,
  });
};

// Default export (tek yerde)
const AuthService = {
  doLogin,
  forgetPassword,
  renewToken,
  getUserProfile,
  editProfile,
  changePassword,
  doLogout,
};

export default AuthService;
